import Script from 'next/script'
import { actionTracking } from 'utils/posthog'
import { notifyIssueSupportOpen } from 'utils/posthog/ap/cardIssue'
import {
  overviewAnalytics,
  overviewSupportQuery
} from 'utils/posthog/ap/overview'
import { apAnalytics } from 'utils/posthog/apAnalytics'
import {
  billsAnalytics,
  billsQueryBucket,
  rememberBillsDestination,
  billsDestinationOpened
} from 'utils/posthog/apBills'

const Zendesk = () => {
  if (process.env.NEXT_PUBLIC_DISABLE_ZENDESK === 'true') {
    return null
  }
  // eslint-disable-next-line alternative/no-invalid-env-usage
  const zendeskKey = process.env.NEXT_PUBLIC_ZENDESK_KEY?.trim()
  if (!zendeskKey) {
    return null
  }

  return (
    <Script
      id='ze-snippet'
      strategy='lazyOnload'
      src={`https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`}
      onLoad={() => {
        // Zendesk injects this global when its widget script loads.
        const zE = (
          window as unknown as {
            zE?: (
              a: string,
              b: string,
              c: (event?: {
                action: string
                properties?: { term?: string }
              }) => void
            ) => void
          }
        ).zE
        let supportAttempt: ReturnType<typeof billsAnalytics.attempt>
        let overviewSupport: ReturnType<typeof overviewAnalytics.attempt>
        const beginSupport = () => {
          if (!supportAttempt) {
            supportAttempt = billsAnalytics.attempt()
            billsAnalytics.emit(
              'supportEntryClicked',
              { entryPoint: 'widget', destination: 'support_widget' },
              supportAttempt
            )
          }
          return supportAttempt
        }
        zE?.('webWidget:on', 'close', () => {
          supportAttempt = undefined
          overviewSupport = undefined
        })
        zE?.('webWidget:on', 'userEvent', event => {
          if (billsAnalytics.context()) {
            if (event?.action === 'Web Widget Opened') {
              beginSupport()
            } else if (event?.action === 'Help Center Search') {
              const query =
                typeof event.properties?.term === 'string'
                  ? event.properties.term
                  : ''
              billsAnalytics.emit(
                'supportSearchCommitted',
                {
                  hasQuery: query.length > 0,
                  queryLengthBucket: billsQueryBucket(query)
                },
                billsAnalytics.attempt()
              )
            } else if (event?.action === 'Contact Form Shown')
              billsAnalytics.emit(
                'supportMessageEntryClicked',
                {},
                billsAnalytics.attempt()
              )
            else if (event?.action === 'Help Center Shown') {
              const context = billsAnalytics.context()
              rememberBillsDestination('help_center', {}, supportAttempt)
              if (context)
                billsDestinationOpened('help_center', context.accountId)
            }
            return
          }
          if (!overviewAnalytics.context()) return
          if (event?.action === 'Web Widget Opened') {
            overviewSupport = overviewAnalytics.attempt()
            overviewAnalytics.note('support')
            apAnalytics.emit(
              'apOverview.supportEntryClicked',
              { entryPoint: 'widget', destinationId: 'support_widget' },
              overviewSupport
            )
          } else if (event?.action === 'Help Center Search') {
            const query =
              typeof event.properties?.term === 'string'
                ? event.properties.term
                : ''
            overviewSupportQuery(query)
          } else if (event?.action === 'Contact Form Shown') {
            overviewAnalytics.note('support')
            apAnalytics.emit(
              'apOverview.supportMessageEntryClicked',
              { entryPoint: 'widget' },
              overviewAnalytics.attempt()
            )
          }
        })
        zE?.('webWidget:on', 'open', () => {
          try {
            const support = overviewAnalytics.context()
              ? overviewSupport
              : undefined
            actionTracking({
              action: 'zendesk_support_chat_opened',
              ...(billsAnalytics.context()
                ? { billsAttempt: beginSupport() }
                : {
                    properties: {
                      entryPoint: 'widget',
                      screenId: support?.screenId,
                      screenVisitId: support?.screenVisitId,
                      originScreenVisitId: support?.originScreenVisitId,
                      userId: support?.userId,
                      accountId: support?.accountId,
                      partnerId: support?.partnerId,
                      environment: support?.environment,
                      trafficClass: support?.trafficClass,
                      actionAttemptId: support?.actionAttemptId,
                      operationId: support?.operationId,
                      inputMethod: support?.inputMethod
                    }
                  })
            })
          } catch {
            // never block the chat from opening on analytics failure
          }
          notifyIssueSupportOpen()
        })
      }}
    />
  )
}
export default Zendesk
